import { PlusCircle } from "react-bootstrap-icons";
import { AsyncButton } from "../../controls";
import { getFromDataPath } from "../../utility";
import { PageRef, PageRefTypeEnum } from "../ars_ldm";
import { useARSContext } from "../contexts";
import { PATCH_ARS_STATE } from "../contexts/interface";
import { ARSText } from "../inputs/ARSText";
import { isDocumentReference } from "../typeGuards/isDocumentReference";
import { PageRefControl } from "./PageRefControl";
import { RemoveArsItemButton } from "./RemoveArsItemButton";

/**
 * Props for the DocumentReferenceControl component.
 */
export interface DocumentReferenceControlProps {
  /** RFC 6901 JSON Pointer path to the DocumentReference in ARSContext state. */
  dataPath: string;
  /** Disabled state. */
  disabled?: boolean;
}

/**
 * A component for managing DocumentReference entities with nested page references.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the DocumentReference in ARSContext state.
 * @param props.disabled - Optional flag to disable editing controls.
 * @returns A React functional component rendering the document reference editor.
 */
export const DocumentReferenceControl = ({
  dataPath,
  disabled = false,
}: DocumentReferenceControlProps): React.ReactElement => {
  const { state, dispatch } = useARSContext();
  const documentReference = getFromDataPath(dataPath, state, isDocumentReference);
  const pageRefs = documentReference?.pageRefs ?? [];
  const idRoute = dataPath.replace(/\//g, "-");

  /**
   * Handles adding a new page reference to the current document reference.
   */
  const handleAddPageRef = (): void => {
    /* istanbul ignore else -- Disabled buttons cannot invoke this handler from the UI */
    if (!disabled && documentReference) {
      const pageRefCount = pageRefs.length;
      const newPageRef: PageRef = {
        refType: PageRefTypeEnum.PhysicalRef,
        label: "",
        pageNames: [],
        pageNumbers: [],
      };

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}/pageRefs/${pageRefCount}`,
        value: newPageRef,
      });
    }
  };

  return (
    <div className="border rounded p-3 mb-3 position-relative">
      <div className="position-absolute top-0 end-0 m-2">
        <RemoveArsItemButton
          dataPath={dataPath}
          disabled={disabled}
        />
      </div>

      <ARSText
        dataPath={`${dataPath}/referenceDocumentId`}
        id={`${idRoute}-reference-document-id`}
        label="Reference document id"
        labelWidth={2}
        placeholder="Enter reference document id"
        disabled={disabled}
      />

      {pageRefs.map((pageRef, index) => (
        <PageRefControl
          key={`${pageRef.refType}-${index}`}
          dataPath={`${dataPath}/pageRefs/${index}`}
          disabled={disabled}
          siblingCount={pageRefs.length}
        />
      ))}

      <AsyncButton
        id={`${idRoute}-add-page-reference`}
        onClick={handleAddPageRef}
        className="d-flex align-items-center btn-sm btn-success"
        disabled={disabled}
      >
        <span className="me-2">Add page reference</span>
        <PlusCircle />
      </AsyncButton>
    </div>
  );
};

DocumentReferenceControl.displayName = "DocumentReferenceControl";
